import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import 'react-table/react-table.css';
import { columnsProptype } from '../common/customPropTypes';
import getFormatter from '../common/formatters';
import defaultStyles from './Grid.module.scss';
import GridView from './GridView';
import createHeaderComponent from './gridcomponents/HeaderComponent';

import withContextModal from './HOCs/withContextModal';
import withDetails from './HOCs/withDetails';
import withPagination from './HOCs/withPagination';
import withRowExpanders from './HOCs/withRowExpanders';
import withScrollbarMeasure from './HOCs/withScrollbarMeasure';
import withFilterable from './HOCs/withFilterable';

const wrapCell = (Component, col, getGridProps) => (Component
    ? (props) => (
        <div
            className={ classNames({
                'cell-wrapper': true,
                'no-padding': col.noPadding,
            }) }
        >
            <Component
                { ...props }
                gridProps={ getGridProps() }
            />
        </div>
    )
    : wrapCell(
        ({ value, original }) => (
            <div
                title={ col.getTooltip ? col.getTooltip(value, original) : getFormatter(col.type)(value) }
                className={ classNames({
                    [getGridProps().styles['cell-text']]: true,
                    'align-right': col.type === 'number',
                    negativeNumber: col.type === 'number' && value < 0
                }) }
            >
                { col.isLink
                    ? (
                        <Link to={ getGridProps().getRowLink({ original }) } className={ getGridProps().styles.cellLink }>
                            { getFormatter(col.type)(value) }
                        </Link>
                    )
                    : getFormatter(col.type)(value) }
            </div>
        ),
        col,
        getGridProps
    ));

const Grid = (props) => {
    const propsRef = useRef(props);
    propsRef.current = props;

    const columns = useMemo(
        () => {
            const mapColumn = (c) => ({
                ...c,
                accessor: (data) => data[c.id],
                Cell: wrapCell(c.renderCell || c.Cell || undefined, c, () => propsRef.current),
                Header: c.Header || createHeaderComponent(c, () => propsRef.current),
                overflow: c.overflow || false,
                padding: c.noPadding || false,
                getGridProps: () => propsRef.current,
            });

            const mapColumnGroup = (c) => (c.columns
                ? {
                    ...c,
                    Header: () => !c.hidden && (
                        <div className={ props.styles['header-content'] }>
                            {
                                (typeof c.Header === 'string')
                                    ? c.Header
                                    : <c.Header />
                            }
                        </div>
                    ),
                    columns: c.columns.map(mapColumnGroup)
                }
                : mapColumn(c)
            );
            return props.columns.map(mapColumnGroup);
        },
        [props.columns, props.styles]
    );

    return (
        <GridView
            { ...props }
            columns={ columns }
            data={ props.rows }
        />
    );
};

const WrappedGrid = fp.compose(
    withRowExpanders,
    withScrollbarMeasure,
    withPagination,
    withContextModal,
    withDetails,
    withFilterable
)(Grid);

WrappedGrid.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    columns: columnsProptype,
    styles: PropTypes.shape({
        'header-content': PropTypes.string
    }),
};

WrappedGrid.defaultProps = {
    rows: [],
    columns: [],
    styles: defaultStyles
};

Grid.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    columns: columnsProptype,
    styles: PropTypes.shape({
        'header-content': PropTypes.string
    }),
};

Grid.defaultProps = {
    rows: [],
    columns: [],
    styles: defaultStyles
};

export default WrappedGrid;
